import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Table from '../../../components/Table/Table';
import AlertModal from '../../../components/AlertModal/AlertModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import Button from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';
import {
  DynamicInput,
  DynamicSelect,
  DynamicCheckbox,
} from '../../../components/Inputs/Inputs';
import { ReactComponent as Edit } from '../../../assets/edit-3.svg';
import { ReactComponent as Close } from '../../../assets/x.svg';
import './HandoffConfig.scss';

const STATUS_LABEL = { online: 'Online', away: 'Ausente', offline: 'Offline' };

const TABS = [
  { value: 'settings', label: 'Definições' },
  { value: 'departments', label: 'Departamentos' },
  { value: 'operators', label: 'Operadores' },
];

function extractErrorMessage(err, fallback = 'Ocorreu um erro') {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error_code || fallback;
  }
  return detail || err?.message || fallback;
}

const initialDeptForm = { name: '', description: '', routing_order: 0, active: true };
const initialEnrollForm = { user_id: '', display_name: '', max_concurrent: 3, department_ids: [] };
const initialCollabForm = {
  username: '',
  name: '',
  email: '',
  password: '',
  display_name: '',
  max_concurrent: 3,
  department_ids: [],
};

export default function HandoffConfig() {
  const { axiosAuth, currentUser, accessibleHotels = [] } = useContext(AuthContext);
  const isSuper = currentUser?.role === 'superadmin';

  // âmbito de hotéis
  const [allHotels, setAllHotels] = useState([]);
  const [groupNameById, setGroupNameById] = useState({});
  const [hotelId, setHotelId] = useState('');
  const [tab, setTab] = useState('settings');

  // licenciamento (só superadmin gere; os outros só veem o estado)
  const [license, setLicense] = useState(null);
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [notLicensed, setNotLicensed] = useState(false);

  // definições
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // departamentos
  const [departments, setDepartments] = useState([]);
  const [deptModal, setDeptModal] = useState({ open: false, mode: 'create', form: initialDeptForm });

  // operadores
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);
  const [opModal, setOpModal] = useState({ open: false, mode: 'enroll', form: initialEnrollForm });

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, err) => {
    setErrorModal({ open: true, title, message: extractErrorMessage(err) });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: '', message: '' }),
    [],
  );

  const [infoModal, setInfoModal] = useState({ open: false, title: '', message: '' });
  const [confirm, setConfirm] = useState({ open: false, kind: null, target: null });

  // utilizadores (para resolver operadores e inscrever novos)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosAuth.get('/users/');
        setUsers(data || []);
      } catch (err) {
        openError('Erro ao carregar utilizadores', err);
      }
    })();
  }, [axiosAuth, openError]);

  // hotéis: superadmin vê todos (com o grupo); admins veem só os acessíveis
  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const [{ data: hotelsData }, { data: groupsData }] = await Promise.all([
          axiosAuth.get('/hotels'),
          axiosAuth.get('/groups'),
        ]);
        const nameByGroup = {};
        (groupsData || []).forEach((g) => {
          nameByGroup[g.id] = g.name;
        });
        setGroupNameById(nameByGroup);
        setAllHotels(hotelsData || []);
      } catch (err) {
        openError('Erro ao carregar hotéis', err);
      }
    })();
  }, [isSuper, axiosAuth, openError]);

  const hotelOptions = useMemo(() => {
    if (isSuper) {
      return allHotels.map((h) => ({
        value: String(h.id),
        label: groupNameById[h.group_id] ? `${h.name} — ${groupNameById[h.group_id]}` : h.name,
      }));
    }
    return accessibleHotels.map((h) => ({ value: String(h.id), label: h.name }));
  }, [isSuper, allHotels, groupNameById, accessibleHotels]);

  useEffect(() => {
    if (!hotelId && hotelOptions.length === 1) {
      setHotelId(hotelOptions[0].value);
    }
  }, [hotelOptions, hotelId]);

  const fetchLicense = useCallback(async () => {
    if (!isSuper || !hotelId) return;
    try {
      const { data } = await axiosAuth.get('/modules/status', {
        params: { hotel_id: Number(hotelId), module_code: 'HUMAN_HANDOFF' },
      });
      setLicense(data?.modules?.[0] || null);
    } catch (err) {
      openError('Erro ao consultar licenciamento', err);
    }
  }, [isSuper, hotelId, axiosAuth, openError]);

  const fetchConfig = useCallback(async () => {
    if (!hotelId) return;
    try {
      const [{ data: settingsData }, { data: deptData }, { data: opData }] = await Promise.all([
        axiosAuth.get('/human-handoff/config/settings', {
          params: { hotel_id: Number(hotelId) },
        }),
        axiosAuth.get('/human-handoff/config/departments', {
          params: { hotel_id: Number(hotelId) },
        }),
        axiosAuth.get('/human-handoff/config/operators', {
          params: { hotel_id: Number(hotelId) },
        }),
      ]);
      setSettings(settingsData);
      setDepartments(deptData || []);
      setOperators(opData || []);
      setNotLicensed(false);
    } catch (err) {
      if (err?.response?.data?.detail?.error_code === 'MODULE_NOT_LICENSED') {
        setNotLicensed(true);
        setSettings(null);
        setDepartments([]);
        setOperators([]);
      } else {
        openError('Erro ao carregar configuração do Atendimento', err);
      }
    }
  }, [hotelId, axiosAuth, openError]);

  useEffect(() => {
    setSettings(null);
    setDepartments([]);
    setOperators([]);
    setNotLicensed(false);
    setLicense(null);
    if (!hotelId) return;
    fetchLicense();
    fetchConfig();
  }, [hotelId, fetchLicense, fetchConfig]);

  useEffect(() => {
    if (!settings) {
      setSettingsForm(null);
      return;
    }
    setSettingsForm({
      operational_enabled: settings.operational_enabled,
      timeout_seconds: settings.timeout_seconds,
      queue_threshold: settings.queue_threshold ?? '',
      collect_contact: settings.collect_contact,
      evaluation_enabled: settings.evaluation_enabled,
      msg_welcome: settings.msg_welcome || '',
      msg_away: settings.msg_away || '',
      msg_queue: settings.msg_queue || '',
      msg_closed: settings.msg_closed || '',
    });
  }, [settings]);

  const userById = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [users]);

  const deptNameById = useMemo(() => {
    const m = {};
    departments.forEach((d) => {
      m[d.id] = d.name;
    });
    return m;
  }, [departments]);

  async function toggleLicense(enabled) {
    setLicenseSaving(true);
    try {
      await axiosAuth.post('/modules/licenses', {
        module_code: 'HUMAN_HANDOFF',
        hotel_id: Number(hotelId),
        enabled,
        reason: 'manual',
      });
      await fetchLicense();
      await fetchConfig();
      setInfoModal({
        open: true,
        title: enabled ? 'Módulo licenciado' : 'Módulo desativado',
        message: enabled
          ? 'O Atendimento Humano foi licenciado para este hotel.'
          : 'O Atendimento Humano foi desativado para este hotel.',
      });
    } catch (err) {
      openError('Erro ao alterar licenciamento', err);
    } finally {
      setLicenseSaving(false);
    }
  }

  async function saveSettings() {
    if (!settingsForm) return;
    setSavingSettings(true);
    try {
      const payload = {
        ...settingsForm,
        timeout_seconds: Number(settingsForm.timeout_seconds),
        queue_threshold:
          settingsForm.queue_threshold === '' ? null : Number(settingsForm.queue_threshold),
      };
      const { data } = await axiosAuth.put('/human-handoff/config/settings', payload, {
        params: { hotel_id: Number(hotelId) },
      });
      setSettings(data);
      setInfoModal({ open: true, title: 'Guardado', message: 'Definições atualizadas.' });
    } catch (err) {
      openError('Erro ao guardar definições', err);
    } finally {
      setSavingSettings(false);
    }
  }

  function openDeptCreate() {
    setDeptModal({ open: true, mode: 'create', form: initialDeptForm });
  }
  function openDeptEdit(d) {
    setDeptModal({
      open: true,
      mode: 'edit',
      form: {
        id: d.id,
        name: d.name,
        description: d.description || '',
        routing_order: d.routing_order,
        active: d.active,
      },
    });
  }

  async function saveDept() {
    const { mode, form } = deptModal;
    if (!form.name.trim()) {
      openError('Validação', new Error('Indique o nome do departamento.'));
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        routing_order: Number(form.routing_order) || 0,
      };
      if (mode === 'create') {
        await axiosAuth.post('/human-handoff/config/departments', payload, {
          params: { hotel_id: Number(hotelId) },
        });
      } else {
        payload.active = form.active;
        await axiosAuth.patch(`/human-handoff/config/departments/${form.id}`, payload, {
          params: { hotel_id: Number(hotelId) },
        });
      }
      setDeptModal({ open: false, mode: 'create', form: initialDeptForm });
      await fetchConfig();
    } catch (err) {
      openError('Erro ao guardar departamento', err);
    }
  }

  function askDeactivateDept(d) {
    setConfirm({ open: true, kind: 'department', target: d });
  }

  function openEnroll() {
    setOpModal({ open: true, mode: 'enroll', form: initialEnrollForm });
  }
  function openCollaborator() {
    setOpModal({ open: true, mode: 'collaborator', form: initialCollabForm });
  }
  function openOpEdit(op) {
    setOpModal({
      open: true,
      mode: 'edit',
      form: {
        id: op.id,
        display_name: op.display_name || '',
        max_concurrent: op.max_concurrent,
        active: op.active,
        department_ids: op.department_ids || [],
      },
    });
  }

  async function saveOperator() {
    const { mode, form } = opModal;
    try {
      if (mode === 'enroll') {
        if (!form.user_id) {
          openError('Validação', new Error('Selecione um utilizador.'));
          return;
        }
        await axiosAuth.post(
          '/human-handoff/config/operators',
          {
            user_id: Number(form.user_id),
            display_name: form.display_name.trim() || null,
            max_concurrent: Number(form.max_concurrent) || 3,
            active: true,
            department_ids: form.department_ids,
          },
          { params: { hotel_id: Number(hotelId) } },
        );
      } else if (mode === 'collaborator') {
        if (!form.username.trim() || !form.name.trim() || !form.email.trim() || !form.password) {
          openError('Validação', new Error('Preencha username, nome, email e password.'));
          return;
        }
        if (form.password.length < 8) {
          openError('Validação', new Error('A password deve ter pelo menos 8 caracteres.'));
          return;
        }
        await axiosAuth.post(
          '/human-handoff/config/collaborators',
          {
            username: form.username.trim(),
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password,
            display_name: form.display_name.trim() || null,
            max_concurrent: Number(form.max_concurrent) || 3,
            department_ids: form.department_ids,
          },
          { params: { hotel_id: Number(hotelId) } },
        );
        // conta nova: atualiza a lista de utilizadores para os próximos formulários
        const { data } = await axiosAuth.get('/users/');
        setUsers(data || []);
      } else {
        await axiosAuth.patch(
          `/human-handoff/config/operators/${form.id}`,
          {
            display_name: form.display_name.trim() || null,
            max_concurrent: Number(form.max_concurrent) || 3,
            active: form.active,
            department_ids: form.department_ids,
          },
          { params: { hotel_id: Number(hotelId) } },
        );
      }
      setOpModal({ open: false, mode: 'enroll', form: initialEnrollForm });
      await fetchConfig();
    } catch (err) {
      openError('Erro ao guardar operador', err);
    }
  }

  function askDeactivateOperator(op) {
    setConfirm({ open: true, kind: 'operator', target: op });
  }

  async function confirmDeactivate() {
    const { kind, target } = confirm;
    try {
      if (kind === 'department') {
        await axiosAuth.delete(`/human-handoff/config/departments/${target.id}`, {
          params: { hotel_id: Number(hotelId) },
        });
      } else if (kind === 'operator') {
        await axiosAuth.delete(`/human-handoff/config/operators/${target.id}`, {
          params: { hotel_id: Number(hotelId) },
        });
      }
      await fetchConfig();
    } catch (err) {
      openError('Erro ao desativar', err);
    } finally {
      setConfirm({ open: false, kind: null, target: null });
    }
  }

  const deptColumns = useMemo(
    () => [
      { key: 'name', label: 'Nome' },
      { key: 'description', label: 'Descrição', render: (d) => d.description || '—' },
      { key: 'routing_order', label: 'Ordem' },
      { key: 'active', label: 'Ativo', render: (d) => (d.active ? 'Sim' : 'Não') },
      {
        key: 'actions',
        label: 'Ações',
        render: (d) => (
          <>
            <button className="action-button edit" onClick={() => openDeptEdit(d)}>
              <Edit />
            </button>
            {d.active && (
              <button className="action-button delete" onClick={() => askDeactivateDept(d)}>
                <Close />
              </button>
            )}
          </>
        ),
      },
    ],
    [],
  );

  const operatorColumns = useMemo(
    () => [
      {
        key: 'user',
        label: 'Utilizador',
        render: (op) => {
          const u = userById[op.user_id];
          return u ? `${u.name} (${u.username})` : `#${op.user_id}`;
        },
      },
      { key: 'display_name', label: 'Nome a mostrar', render: (op) => op.display_name || '—' },
      { key: 'status', label: 'Estado', render: (op) => STATUS_LABEL[op.status] || op.status },
      { key: 'max_concurrent', label: 'Máx. simultâneas' },
      {
        key: 'departments',
        label: 'Departamentos',
        render: (op) =>
          (op.department_ids || []).map((id) => deptNameById[id]).filter(Boolean).join(', ') ||
          '—',
      },
      { key: 'active', label: 'Ativo', render: (op) => (op.active ? 'Sim' : 'Não') },
      {
        key: 'actions',
        label: 'Ações',
        render: (op) => (
          <>
            <button className="action-button edit" onClick={() => openOpEdit(op)}>
              <Edit />
            </button>
            {op.active && (
              <button className="action-button delete" onClick={() => askDeactivateOperator(op)}>
                <Close />
              </button>
            )}
          </>
        ),
      },
    ],
    [userById, deptNameById],
  );

  const deptCheckboxes = (selected, onChange) => (
    <div className="checkbox-grid">
      {departments
        .filter((d) => d.active)
        .map((d) => {
          const checked = selected.includes(d.id);
          return (
            <label key={d.id} className="checkbox-item">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  const set = new Set(selected);
                  if (e.target.checked) set.add(d.id);
                  else set.delete(d.id);
                  onChange(Array.from(set));
                }}
              />
              {d.name}
            </label>
          );
        })}
      {departments.filter((d) => d.active).length === 0 && (
        <div className="help">Sem departamentos ativos.</div>
      )}
    </div>
  );

  return (
    <div className="handoff-config-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {infoModal.open && (
        <AlertModal
          variant="info"
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: '', message: '' })}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title={confirm.kind === 'department' ? 'Desativar departamento' : 'Desativar operador'}
          variant="danger"
          message={
            confirm.kind === 'department'
              ? 'Deseja desativar este departamento? Deixa de poder ser escolhido, mas não é apagado.'
              : 'Deseja desativar este operador? Deixa de receber novas conversas, mas o histórico mantém-se.'
          }
          secText="Fechar"
          varText="Desativar"
          onCancel={() => setConfirm({ open: false, kind: null, target: null })}
          onConfirm={confirmDeactivate}
        />
      )}

      <PageTitle link="" title="Configurar Atendimento" />

      {hotelOptions.length > 1 && (
        <div className="hotel-selector">
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            options={hotelOptions}
          />
        </div>
      )}

      {!hotelId ? (
        <div className="empty-page">
          <p>Escolhe um hotel para configurar o Atendimento.</p>
        </div>
      ) : (
        <>
          {isSuper && (
            <div className="license-panel">
              <div className="license-status">
                <span className={`badge ${license?.licensed ? 'on' : 'off'}`}>
                  {license?.licensed ? 'Módulo licenciado' : 'Módulo não licenciado'}
                </span>
              </div>
              <Button
                type="button"
                variant={license?.licensed ? 'secondary' : 'primary'}
                disabled={licenseSaving}
                onClick={() => toggleLicense(!license?.licensed)}
              >
                {license?.licensed ? 'Desativar módulo' : 'Licenciar módulo'}
              </Button>
            </div>
          )}

          {notLicensed ? (
            <div className="empty-page">
              <p>
                {isSuper
                  ? 'Licencia o módulo Atendimento Humano acima para configurar este hotel.'
                  : 'Este hotel não tem o módulo Atendimento Humano licenciado. Fala com um SuperAdmin para o ativar.'}
              </p>
            </div>
          ) : (
            <>
              <div className="tabs">
                {TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={`tab-btn ${tab === t.value ? 'active' : ''}`}
                    onClick={() => setTab(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === 'settings' && settingsForm && (
                <div className="settings-panel">
                  <div className="row checkbox-row">
                    <DynamicCheckbox
                      label="Atendimento operacional (liga/desliga a fila para os hóspedes)"
                      name="operational_enabled"
                      checked={settingsForm.operational_enabled}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, operational_enabled: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row checkbox-row">
                    <DynamicCheckbox
                      label="Recolher contacto do hóspede antes de entrar na fila"
                      name="collect_contact"
                      checked={settingsForm.collect_contact}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, collect_contact: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row checkbox-row">
                    <DynamicCheckbox
                      label="Pedir avaliação ao hóspede no fecho da conversa"
                      name="evaluation_enabled"
                      checked={settingsForm.evaluation_enabled}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, evaluation_enabled: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row two-col">
                    <DynamicInput
                      type="number"
                      label="Timeout de inatividade (segundos)"
                      name="timeout_seconds"
                      value={settingsForm.timeout_seconds}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, timeout_seconds: value }))
                      }
                      min={10}
                      max={86400}
                      disabled={savingSettings}
                    />
                    <DynamicInput
                      type="number"
                      label="Aviso de fila a partir de (nº pedidos, opcional)"
                      name="queue_threshold"
                      value={settingsForm.queue_threshold}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, queue_threshold: value }))
                      }
                      min={0}
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row">
                    <DynamicInput
                      type="text"
                      label="Mensagem de boas-vindas"
                      name="msg_welcome"
                      value={settingsForm.msg_welcome}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, msg_welcome: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row">
                    <DynamicInput
                      type="text"
                      label="Mensagem fora de horário"
                      name="msg_away"
                      value={settingsForm.msg_away}
                      onChange={(name, value) => setSettingsForm((p) => ({ ...p, msg_away: value }))}
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row">
                    <DynamicInput
                      type="text"
                      label="Mensagem de fila de espera"
                      name="msg_queue"
                      value={settingsForm.msg_queue}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, msg_queue: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="row">
                    <DynamicInput
                      type="text"
                      label="Mensagem de conversa fechada"
                      name="msg_closed"
                      value={settingsForm.msg_closed}
                      onChange={(name, value) =>
                        setSettingsForm((p) => ({ ...p, msg_closed: value }))
                      }
                      disabled={savingSettings}
                    />
                  </div>
                  <div className="settings-actions">
                    <Button type="button" onClick={saveSettings} disabled={savingSettings}>
                      Guardar definições
                    </Button>
                  </div>
                </div>
              )}

              {tab === 'departments' && (
                <div className="section-panel">
                  <div className="section-header">
                    <Button type="button" onClick={openDeptCreate}>
                      Novo departamento
                    </Button>
                  </div>
                  <div className="container">
                    <Table columns={deptColumns} data={departments} />
                  </div>
                </div>
              )}

              {tab === 'operators' && (
                <div className="section-panel">
                  <div className="section-header">
                    <Button type="button" onClick={openEnroll}>
                      Inscrever utilizador existente
                    </Button>
                    <Button type="button" variant="secondary" onClick={openCollaborator}>
                      Criar colaborador
                    </Button>
                  </div>
                  <div className="container">
                    <Table columns={operatorColumns} data={operators} />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {deptModal.open && (
        <Modal
          isVisible={deptModal.open}
          onClose={() => setDeptModal({ open: false, mode: 'create', form: initialDeptForm })}
          title={deptModal.mode === 'create' ? 'Novo departamento' : 'Editar departamento'}
          onSave={saveDept}
          saveText={deptModal.mode === 'create' ? 'Criar' : 'Guardar'}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveDept();
            }}
          >
            <div className="row">
              <DynamicInput
                type="text"
                label="Nome"
                name="name"
                value={deptModal.form.name}
                onChange={(name, value) =>
                  setDeptModal((p) => ({ ...p, form: { ...p.form, name: value } }))
                }
                required
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Descrição"
                name="description"
                value={deptModal.form.description}
                onChange={(name, value) =>
                  setDeptModal((p) => ({ ...p, form: { ...p.form, description: value } }))
                }
              />
            </div>
            <div className="row">
              <DynamicInput
                type="number"
                label="Ordem de encaminhamento"
                name="routing_order"
                value={deptModal.form.routing_order}
                onChange={(name, value) =>
                  setDeptModal((p) => ({ ...p, form: { ...p.form, routing_order: value } }))
                }
              />
            </div>
            {deptModal.mode === 'edit' && (
              <div className="row checkbox-row">
                <DynamicCheckbox
                  label="Ativo"
                  name="active"
                  checked={deptModal.form.active}
                  onChange={(name, value) =>
                    setDeptModal((p) => ({ ...p, form: { ...p.form, active: value } }))
                  }
                />
              </div>
            )}
          </form>
        </Modal>
      )}

      {opModal.open && (
        <Modal
          isVisible={opModal.open}
          onClose={() => setOpModal({ open: false, mode: 'enroll', form: initialEnrollForm })}
          title={
            opModal.mode === 'enroll'
              ? 'Inscrever utilizador existente'
              : opModal.mode === 'collaborator'
              ? 'Criar colaborador'
              : 'Editar operador'
          }
          onSave={saveOperator}
          saveText={opModal.mode === 'edit' ? 'Guardar' : 'Inscrever'}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveOperator();
            }}
          >
            {opModal.mode === 'enroll' && (
              <div className="row roles">
                <label className="label">Utilizador</label>
                <select
                  value={opModal.form.user_id}
                  onChange={(e) =>
                    setOpModal((p) => ({ ...p, form: { ...p.form, user_id: e.target.value } }))
                  }
                >
                  <option value="">Selecionar um utilizador</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.username}) — {u.role}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {opModal.mode === 'collaborator' && (
              <>
                <div className="row">
                  <DynamicInput
                    type="text"
                    label="Username"
                    name="username"
                    value={opModal.form.username}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, username: value } }))
                    }
                    required
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="text"
                    label="Nome"
                    name="name"
                    value={opModal.form.name}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, name: value } }))
                    }
                    required
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="email"
                    label="Email"
                    name="email"
                    value={opModal.form.email}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, email: value } }))
                    }
                    required
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="password"
                    label="Password"
                    name="password"
                    value={opModal.form.password}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, password: value } }))
                    }
                    required
                  />
                </div>
              </>
            )}

            {(opModal.mode === 'enroll' || opModal.mode === 'collaborator' || opModal.mode === 'edit') && (
              <>
                <div className="row">
                  <DynamicInput
                    type="text"
                    label="Nome a mostrar ao hóspede (opcional)"
                    name="display_name"
                    value={opModal.form.display_name}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, display_name: value } }))
                    }
                  />
                </div>
                <div className="row">
                  <DynamicInput
                    type="number"
                    label="Conversas simultâneas máximas"
                    name="max_concurrent"
                    value={opModal.form.max_concurrent}
                    onChange={(name, value) =>
                      setOpModal((p) => ({ ...p, form: { ...p.form, max_concurrent: value } }))
                    }
                    min={1}
                    max={50}
                  />
                </div>
                <div className="row checkbox">
                  <label>Departamentos</label>
                  {deptCheckboxes(opModal.form.department_ids, (ids) =>
                    setOpModal((p) => ({ ...p, form: { ...p.form, department_ids: ids } })),
                  )}
                </div>
              </>
            )}

            {opModal.mode === 'edit' && (
              <div className="row checkbox-row">
                <DynamicCheckbox
                  label="Ativo"
                  name="active"
                  checked={opModal.form.active}
                  onChange={(name, value) =>
                    setOpModal((p) => ({ ...p, form: { ...p.form, active: value } }))
                  }
                />
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
